// ─── Storefront API — replaces the old per-variant Liquid JSON blobs ───────
//
// 2026-09-03 perf investigation: this configurator's Liquid used to loop
// EVERY variant of the product (up to 300+ on some products) reading 6+
// metafields each — ilva/ax.price_schedule, ilva/ax.related_campaigns,
// perfion.variant_rich_info, perfion.prod_config_rel_prod1/2 (+ labels) —
// server-side, on every single page load, whether or not a shopper ever
// opened the fabric modal. Since the configurator already requires
// JavaScript to function (there's no working no-JS interaction, only the
// <noscript> fallback dropdown in product-configurator.liquid), that data now
// loads via the Storefront API instead, using the same token/fetch pattern
// already established in relewise.js and store-locator.js.
//
// Split into three requests, NOT one, because a single query nesting
// references(first: N) 5 levels deep inside variants(first: 250) blows
// Shopify's ~1000-point query-cost ceiling by orders of magnitude for the
// 200-300+ variant products (Flexlux Clement, several Signatur chaiselong
// series) — Shopify would reject it outright, not just serve it slowly:
//
//   1. CONFIGURATOR_VARIANTS_QUERY — ALL variants, but scalar fields only
//      (id/price/available/options/image/schedule string) — no nested
//      reference connections, so it scales safely to 250+.
//   2. CONFIGURATOR_RICH_DATA_QUERY — richInfo (fabric swatch image/name) +
//      accessory slots, fetched via nodes(ids:[...]) for a SMALL, bounded
//      set: one variant per distinct option1/fabric value (typically
//      15-40, mirroring the old Liquid's own "one card per distinct
//      option1" dedup for the fabric grid) plus the current variant (for
//      its accessory panel). Awaited before the fabric modal can ever open,
//      so there's no empty-grid flash.
//   3. Campaign badges — fetched lazily, only once the fabric modal
//      actually opens (and again after a correction moves the selection to
//      a new firmness/mattress combo), for the small set of variants the
//      grid actually reads from — one per distinct fabric, see
//      _gridVariantsForCurrentOptions and _loadComboCampaigns.
//
// Every response is transformed into the EXACT same per-variant shape the
// old Liquid JSON produced (see _transformVariant/_buildFabricData below),
// so none of the rendering/interaction logic elsewhere in this file needed
// to change — only how this.variants/this.fabricData get populated, and when.
const CONFIGURATOR_VARIANTS_QUERY = `
  query ConfiguratorVariants($id: ID!) {
    product(id: $id) {
      variants(first: 250) {
        nodes {
          id
          availableForSale
          price { amount }
          compareAtPrice { amount }
          quantityAvailable
          selectedOptions { name value }
          image { id url }
          ilvaSchedule: metafield(namespace: "ilva", key: "price_schedule") { value }
          axSchedule: metafield(namespace: "ax", key: "price_schedule") { value }
        }
      }
    }
  }
`;

const CONFIGURATOR_RICH_DATA_QUERY = `
  query ConfiguratorRichData($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        richInfo: metafield(namespace: "perfion", key: "variant_rich_info") {
          references(first: 10) {
            nodes {
              ... on Metaobject {
                fields {
                  key
                  value
                  reference { ... on MediaImage { image { url } } }
                }
              }
            }
          }
        }
        slot1Label: metafield(namespace: "perfion", key: "prod_config_rel_prod1_label") { value }
        slot2Label: metafield(namespace: "perfion", key: "prod_config_rel_prod2_label") { value }
        slot1: metafield(namespace: "perfion", key: "prod_config_rel_prod1") {
          references(first: 20) {
            nodes {
              ... on ProductVariant {
                id
                title
                availableForSale
                price { amount }
                image { url }
                product { id handle featuredImage { url } }
              }
            }
          }
        }
        slot2: metafield(namespace: "perfion", key: "prod_config_rel_prod2") {
          references(first: 20) {
            nodes {
              ... on ProductVariant {
                id
                title
                availableForSale
                price { amount }
                image { url }
                product { id handle featuredImage { url } }
              }
            }
          }
        }
      }
    }
  }
`;

const CONFIGURATOR_CAMPAIGNS_QUERY = `
  query ConfiguratorCampaigns($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        ilvaCampaigns: metafield(namespace: "ilva", key: "related_campaigns") {
          references(first: 5) {
            nodes {
              ... on Metaobject {
                id
                fields { key value }
              }
            }
          }
        }
        axCampaigns: metafield(namespace: "ax", key: "related_campaigns") {
          references(first: 5) {
            nodes {
              ... on Metaobject {
                id
                fields { key value }
              }
            }
          }
        }
      }
    }
  }
`;

function configuratorParseGid(gid) {
  const match = /\/(\d+)$/.exec(gid || '');
  return match ? parseInt(match[1], 10) : null;
}

// Storefront API money fields return a decimal string ("1999.0"); every
// price in this.variants/this.fabricData is cents (int), matching the old
// Liquid JSON — all downstream math (_formatMoney, price deltas, cart totals)
// depends on that.
function configuratorCentsFromAmount(amount) {
  if (amount == null) return 0;
  return Math.round(parseFloat(amount) * 100);
}

// Reduces a Metaobject's `fields { key value reference {...} }` array into a
// plain { key: value } object, preferring an image reference's resolved URL
// over the field's raw value (which for an image/file field is just an id).
function configuratorMetaobjectFields(metaobject) {
  const out = {};
  (metaobject?.fields || []).forEach((f) => {
    out[f.key] = f.reference?.image?.url ?? f.value;
  });
  return out;
}

// Storefront API's metaobject field `value` is always a raw string; a
// list-type field (base_color, material) comes back JSON-encoded (e.g.
// '["Red"]'), unlike Liquid's `mo.field.value`, which resolves a list field
// to a real array. Parse it back into one here so downstream Array.isArray
// checks (filter options, the fabric grid, the detail popup) work.
function configuratorParseListField(value) {
  if (typeof value !== 'string' || !value.startsWith('[')) return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function configuratorEscapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Renders a rich_text_field's parsed JSON tree to HTML, mirroring what the
// `metafield_tag` Liquid filter does server-side — the Storefront API only
// ever gives us the raw JSON, so this has to happen client-side.
function configuratorRichTextNodeToHtml(node) {
  if (!node) return '';
  if (node.type === 'text') {
    let text = configuratorEscapeHtml(node.value || '').replace(/\n/g, '<br>');
    if (node.bold) text = `<strong>${text}</strong>`;
    if (node.italic) text = `<em>${text}</em>`;
    return text;
  }

  const children = (node.children || []).map(configuratorRichTextNodeToHtml).join('');

  switch (node.type) {
    case 'root':
      return children;
    case 'paragraph':
      return `<p>${children}</p>`;
    case 'heading': {
      const level = node.level >= 1 && node.level <= 6 ? node.level : 2;
      return `<h${level}>${children}</h${level}>`;
    }
    case 'list':
      return node.listType === 'ordered' ? `<ol>${children}</ol>` : `<ul>${children}</ul>`;
    case 'list-item':
      return `<li>${children}</li>`;
    case 'link': {
      const url = configuratorEscapeHtml(node.url || '');
      const target = node.target === '_blank' ? ' target="_blank" rel="noopener"' : '';
      return `<a href="${url}"${target}>${children}</a>`;
    }
    default:
      return children;
  }
}

// A rich_text_field's raw value is a JSON string; a plain multi-line text
// field's isn't, so a description that fails to parse is rendered as plain
// text instead of being dropped.
function configuratorRenderRichText(raw) {
  if (!raw) return '';
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return configuratorEscapeHtml(raw).replace(/\n/g, '<br>');
  }
  return configuratorRichTextNodeToHtml(parsed);
}

/**
 * Product Configurator custom element.
 * Handles size, firmness (dual-side), fabric/color modal, topmattress, accessory slots, and upsell selection.
 */
class ProductConfigurator extends HTMLElement {
  async connectedCallback() {
    this.currentVariantId = parseInt(this.dataset.currentVariant, 10);
    // Availability labels come in as attributes because the panels are built in
    // JS, which has no access to the `t` filter.
    this._soldOutLabel = this.dataset.labelSoldOut || '';
    this._unavailableLabel = this.dataset.labelUnavailable || '';
    this._shortDeliveryUnavailableLabel = this.dataset.labelShortDeliveryUnavailable || '';
    this._fabricNoResultsLabel = this.dataset.labelFabricNoResults || '';
    this._firmnessSide1Label = this.dataset.labelFirmnessSide1 || '';
    this._firmnessSide2Label = this.dataset.labelFirmnessSide2 || '';
    this.selectedAddons = { slot1: null, slot2: null };
    // What the shopper last explicitly picked per slot, kept even when a
    // variant change drops the live selection — see _rematchAddon.
    this._addonMemory = { slot1: null, slot2: null };
    this.selectedUpsells = new Map();
    this.pendingFabric = null;

    // Fabric modal's "short delivery" checkbox: variantId -> in stock at the
    // central warehouse, fetched live from the Storefront API and judged by the
    // PDP's own rule (see _loadWarehouseStock).
    this._shopDomain = this.dataset.shopDomain || '';
    this._storefrontToken = this.dataset.storefrontToken || '';
    this._warehouseAxId = this.dataset.warehouseAxId || '';
    this._warehouseStock = new Map();
    // Which option2/option3 combo _warehouseStock was fetched for — a
    // correction can change either while a fetch for the old combo is still
    // in flight, so the grid needs to know when the map it's holding no
    // longer matches the current selection (see _renderFabricGrid).
    this._warehouseStockComboKey = null;

    this.variants = [];
    this.fabricData = [];
    await this._loadConfiguratorData();
    this.selectedOptions = this._optionsFromVariant(this._findVariantById(this.currentVariantId));

    this._initFirmness();
    this._initVariantPanels();
    this._initAddonPanels();
    this._initUpsellPanel();
    this._initToggles();
    this._initSizeOptions();
    this._initModal();
    this._updateTotalPrice();
    this._updateCurrentSizePrice();
  }

  // ─── Storefront API data load ──────────────────────────────────────────────

  async _configuratorFetch(query, variables) {
    if (!this._shopDomain || !this._storefrontToken) {
      // eslint-disable-next-line no-console
      console.error('[product-configurator] missing shop domain or storefront token — data-shop-domain/data-storefront-token empty on <product-configurator>.');
      return null;
    }
    let json;
    try {
      const res = await fetch(`https://${this._shopDomain}/api/2026-07/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this._storefrontToken,
        },
        body: JSON.stringify({ query, variables }),
      });
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.error(`[product-configurator] Storefront API request failed: ${res.status} ${res.statusText}`);
        return null;
      }
      json = await res.json();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[product-configurator] Storefront API request threw', err);
      return null;
    }
    // Shopify returns HTTP 200 even when a field/type is wrong — the real
    // problem shows up here, not as a rejected fetch, and `data` can be null
    // or partial. Surfacing this was missing entirely before, which is why
    // a bad field name failed completely silently (2026-09-03 perf
    // investigation). Note: a metafield that legitimately has data can still
    // come back null here with NO entry in json.errors — Shopify silently
    // omits a field whose metafield definition hasn't had "Storefront API
    // access" enabled in admin (Settings → Custom data), which is what
    // actually turned out to cause slot1/slot2 not populating once. Check
    // that toggle before assuming the query itself is wrong.
    if (json?.errors?.length) {
      // eslint-disable-next-line no-console
      console.error('[product-configurator] Storefront API returned errors:', json.errors);
    }
    return json;
  }

  // Populates this.variants/this.fabricData in the exact shape the old
  // server-rendered Liquid JSON used to produce. Falls back to an empty
  // configurator (the row headers stay visible but non-interactive) if the
  // token/domain are missing or a request fails — the <noscript> native
  // <select> in product-configurator.liquid is the real fallback for that
  // case, but this element only renders when JS is already running, so
  // there's no better degraded state to build here beyond "don't throw".
  async _loadConfiguratorData() {
    if (!this.dataset.productId) return;

    const variantsJson = await this._configuratorFetch(CONFIGURATOR_VARIANTS_QUERY, {
      id: `gid://shopify/Product/${this.dataset.productId}`,
    });
    const nodes = variantsJson?.data?.product?.variants?.nodes || [];
    this.variants = nodes.map((node) => this._transformVariant(node));
    if (!this.variants.length) return;

    // Rich data (fabric image/name + accessory slots) only needs one variant
    // per distinct fabric — mirrors the old Liquid's own dedup for the fabric
    // grid — plus the current variant, for its accessory panel. Bounded set
    // regardless of total variant count, unlike the cheap fetch above.
    const seenOption1 = new Set();
    const richIds = [];
    this.variants.forEach((v) => {
      if (v.option1 != null && !seenOption1.has(v.option1)) {
        seenOption1.add(v.option1);
        richIds.push(v.id);
      }
    });
    if (!richIds.includes(this.currentVariantId)) richIds.push(this.currentVariantId);

    const richJson = await this._configuratorFetch(CONFIGURATOR_RICH_DATA_QUERY, {
      ids: richIds.map((id) => `gid://shopify/ProductVariant/${id}`),
    });
    this._mergeRichData(richJson?.data?.nodes || []);

    this.fabricData = this._buildFabricData(this.variants);
  }

  // Lazily fetches campaign badges for the current combination of every
  // distinct fabric — same bounded comboVariants set _loadWarehouseStock
  // computes, and same trigger point (fabric modal open). Campaigns are the
  // one piece of data that's both per-exact-variant (not fabric-level, since
  // eligibility can differ by firmness/mattress) AND only visible once the
  // modal is open, so unlike richInfo there's no need to have it ready at
  // page load.
  async _loadComboCampaigns(variants) {
    // Re-run after every correction, so only fetch what hasn't been fetched
    // yet — a variant with no campaigns is marked too, or it would be
    // re-requested on every pass.
    const pending = variants.filter((v) => !v.campaignsFetched);
    if (!pending.length) return;
    pending.forEach((v) => { v.campaignsFetched = true; });

    const json = await this._configuratorFetch(CONFIGURATOR_CAMPAIGNS_QUERY, {
      ids: pending.map((v) => `gid://shopify/ProductVariant/${v.id}`),
    });
    // _configuratorFetch swallows failures into null; leave those variants
    // eligible for the next pass rather than permanently badge-less.
    if (!json) {
      pending.forEach((v) => { v.campaignsFetched = false; });
      return;
    }
    const nodes = json?.data?.nodes || [];
    let changed = false;
    nodes.forEach((node) => {
      if (!node) return;
      const variant = this._findVariantById(configuratorParseGid(node.id));
      if (!variant) return;
      const campaigns = this._extractCampaigns(node);
      if (campaigns.length) {
        variant.campaigns = campaigns;
        changed = true;
      }
    });
    if (changed) this._refreshFabricGrid();
  }

  // Fetches one variant's own richInfo/slot1/slot2 on demand when it wasn't
  // part of the bounded page-load sample (one representative per fabric) —
  // see the comment on the call site in _updateVariantFromOptions. Guarded
  // per-variant so rapidly toggling firmness back and forth doesn't fire a
  // duplicate request for the same not-yet-fetched variant.
  _ensureVariantRichDataFetched(variant) {
    if (variant.richFetched || variant._richFetchInFlight) return variant._richFetchPromise || Promise.resolve();
    variant._richFetchInFlight = true;
    variant._richFetchPromise = (async () => {
      try {
        const json = await this._configuratorFetch(CONFIGURATOR_RICH_DATA_QUERY, {
          ids: [`gid://shopify/ProductVariant/${variant.id}`],
        });
        this._mergeRichData(json?.data?.nodes || []);
      } finally {
        variant._richFetchInFlight = false;
      }
      // Only react if the fetch actually changed what's selected — a
      // remembered accessory re-selected against the fresh options (see
      // _rematchAddon). Dispatching unconditionally would let
      // product-form.js clear aria-disabled on the add-to-cart buttons while
      // a submit is in flight, for no reason.
      const before = this._addonSelectionKey();
      this._refreshAddonPanels();
      if (this._addonSelectionKey() !== before) {
        this._updateTotalPrice();
        this._dispatchSelectionChanged();
      }
    })();
    return variant._richFetchPromise;
  }

  _addonSelectionKey() {
    return ['slot1', 'slot2'].map((key) => this.selectedAddons[key]?.variantId ?? '').join('|');
  }

  // Resolves once the current variant's accessory slot data is loaded, so a
  // caller building a cart request (product-form.js) never reads
  // getCartItems() in the window between a variant change and the on-demand
  // slot fetch — where a remembered accessory isn't re-selected yet and
  // would silently be left out of the cart.
  whenAddonsReady() {
    const variant = this._findVariantById(this.currentVariantId);
    if (!variant || variant.richFetched) return Promise.resolve();
    return this._ensureVariantRichDataFetched(variant);
  }

  _extractCampaigns(node) {
    const campaignNodes = node.ilvaCampaigns?.references?.nodes?.length
      ? node.ilvaCampaigns.references.nodes
      : (node.axCampaigns?.references?.nodes || []);
    return campaignNodes.map((c) => {
      const f = configuratorMetaobjectFields(c);
      // Same dual-key manual-label override as product-label.liquid/card-price.liquid.
      const label = (f.override_label === 'true' && (f.label_text_manual || f.olina_s_test)) || f.label_text;
      let color = '';
      if (f.label_colour) {
        try { color = JSON.parse(f.label_colour).rgb || ''; } catch { color = ''; }
      }
      // endDate (the date field, YYYY-MM-DD) lets resolveScheduleState end a
      // regular row that carries no campaignEndDate of its own — same as the
      // card-price.liquid payload (ClickUp 869fbtgaa).
      return {
        id: c.id,
        label,
        type: f.campaign_type,
        ...(f.end_date ? { endDate: f.end_date } : {}),
        ...(color ? { color } : {}),
      };
    });
  }

  // Merges CONFIGURATOR_RICH_DATA_QUERY's nodes (richInfo + accessory slots)
  // into the matching this.variants entries by id.
  _mergeRichData(nodes) {
    nodes.forEach((node) => {
      if (!node) return;
      const variant = this._findVariantById(configuratorParseGid(node.id));
      if (!variant) return;

      variant.richFetched = true;
      variant.richInfo = (node.richInfo?.references?.nodes || []).map((m) => {
        const f = configuratorMetaobjectFields(m);
        return {
          name: f.name,
          image: f.image || null,
          secondaryImage: f.secondary_image || null,
          description: f.description || null,
          baseColor: configuratorParseListField(f.base_color) || null,
          material: configuratorParseListField(f.material) || null,
          martindale: f.martindale || null,
          lightFastness: f.light_fastness || null,
          pilling: f.pilling || null,
          deliveryNote: f.delivery_note || null,
          badge: f.badge || null,
        };
      });

      const buildSlot = (labelField, slotField) => ({
        label: labelField?.value || null,
        options: (slotField?.references?.nodes || [])
          .filter((v) => v.availableForSale)
          .map((v, index) => ({
            variantId: configuratorParseGid(v.id),
            productId: v.product?.id ? configuratorParseGid(v.product.id) : null,
            productHandle: v.product?.handle || null,
            index,
            title: v.title,
            price: configuratorCentsFromAmount(v.price?.amount),
            image: v.image?.url || v.product?.featuredImage?.url || null,
            imageAlt: v.title,
          })),
      });
      variant.slot1 = buildSlot(node.slot1Label, node.slot1);
      variant.slot2 = buildSlot(node.slot2Label, node.slot2);
    });

    // Fabric representatives share their richInfo/slots with every other
    // variant of the same option1 — a plain color/firmness/mattress
    // combination reuses the swatch's image/name, not just the one
    // representative variant id we actually fetched.
    const byOption1 = new Map();
    this.variants.forEach((v) => {
      if (v.richInfo?.length || v.slot1?.options?.length || v.slot2?.options?.length) {
        byOption1.set(v.option1, v);
      }
    });
    // Slots deliberately are NOT backfilled here — they can legitimately
    // differ by firmness/mattress (per the original Liquid's design), so
    // only sharing richInfo (fabric-level only) across same-option1 variants
    // is correct; a variant with no slot data simply had none fetched.
    this.variants.forEach((v) => {
      if (v.richInfo?.length) return;
      const source = byOption1.get(v.option1);
      if (source) v.richInfo = source.richInfo;
    });
  }

  // One GraphQL variant node (from the cheap CONFIGURATOR_VARIANTS_QUERY) →
  // the old per-variant JSON shape's scalar fields. richInfo/slot1/slot2 are
  // filled in afterward by _mergeRichData; campaigns by _loadComboCampaigns.
  _transformVariant(node) {
    const id = configuratorParseGid(node.id);
    const [opt1, opt2, opt3] = node.selectedOptions.map((o) => o.value);

    const scheduleRaw = node.ilvaSchedule?.value || node.axSchedule?.value;
    let schedule;
    if (scheduleRaw) {
      try { schedule = JSON.parse(scheduleRaw); } catch { schedule = undefined; }
    }

    return {
      id,
      available: node.availableForSale,
      price: configuratorCentsFromAmount(node.price?.amount),
      compareAtPrice: configuratorCentsFromAmount(node.compareAtPrice?.amount),
      inventoryQuantity: node.quantityAvailable || 0,
      // Storefront API's quantityAvailable is only meaningful when Shopify
      // tracks inventory for the variant, same as the old inventoryManagement
      // check — quantityAvailable is null (not 0) for untracked variants.
      inventoryManaged: node.quantityAvailable != null,
      option1: opt1 ?? null,
      option2: opt2 ?? null,
      option3: opt3 ?? null,
      featuredImage: node.image?.url || null,
      // Matches the numeric id space media-gallery.js reads off
      // data-media-id (Shopify's Image/MediaImage ids share that space with
      // product.media in Liquid) — used as the gallery's fallback "jump to
      // this image" target when the variant has no native/DAM item tagged
      // with its id directly (see applyVariantMedia in media-gallery.js).
      featuredMediaId: node.image?.id ? configuratorParseGid(node.image.id) : null,
      ...(schedule ? { schedule } : {}),
      richInfo: [],
      slot1: { label: null, options: [] },
      slot2: { label: null, options: [] },
      // Distinguishes "never fetched" from "fetched and genuinely empty" —
      // a variant can legitimately have no slot1/slot2 data at all, so an
      // empty options array alone can't tell _ensureVariantRichDataFetched
      // whether it still needs to ask for this variant's own data.
      richFetched: false,
    };
  }

  // Mirrors the old Liquid's "one card per distinct option1 value" dedup for
  // the fabric grid (product-configurator.liquid used to build this
  // server-side as data-configurator-fabric).
  _buildFabricData(variants) {
    const seen = new Set();
    const fabrics = [];
    variants.forEach((v) => {
      if (v.option1 == null || seen.has(v.option1)) return;
      seen.add(v.option1);
      fabrics.push({
        option1: v.option1,
        variantId: v.id,
        price: v.price,
        compareAtPrice: v.compareAtPrice,
        variantImage: v.featuredImage,
        richInfo: v.richInfo,
      });
    });
    return fabrics;
  }

  // ─── PDP total price (variant + selected accessory/upsell prices) ─────────
  //
  // The configurator PDP shows two prices: the regular price block keeps
  // showing the plain variant price (server-rendered, never touched here), and
  // main-product.liquid's `total-price` block (`[data-configurator-total-price]`)
  // shows variant + selected accessory slots + upsells. That block carries no
  // data-dynamic-variant-content attribute, so it survives the variant-change
  // section swap and only needs updating from here.
  //
  // The base follows the price block: on a member day (per the shared
  // price-schedule resolver in main.js — the same Member > Sale > Regular
  // precedence <pdp-price-schedule> uses to swap in the member overlay) the
  // member price is the base, otherwise the variant's catalog price. Add-on
  // and upsell prices are always their catalog price: their own schedules are
  // never fetched, and the configurator shows them at catalog price too.

  _updateTotalPrice() {
    const totalEl = (this.closest('.shopify-section') || document).querySelector('[data-configurator-total-price]');
    if (!totalEl || typeof window.formatMoney !== 'function') return;
    const valueEl = totalEl.querySelector('.js-value') || totalEl;

    const variant = this._findVariantById(this.currentVariantId);
    if (!variant || !variant.price) {
      // No variant / zero price = missing catalog data (see price.liquid) — a
      // total built on it would be wrong, so hide the block instead.
      totalEl.hidden = true;
      return;
    }

    let totalCents = this._headlinePriceCents(variant);
    Object.values(this.selectedAddons).forEach((addon) => {
      if (addon) totalCents += addon.price;
    });
    this.selectedUpsells.forEach((upsell) => {
      totalCents += upsell.price;
    });

    valueEl.textContent = window.formatMoney(totalCents / 100);
    totalEl.hidden = false;
  }

  // The price the PDP price block headlines for a variant: the member price on
  // a member day (Member > Sale > Regular, via the shared resolver), otherwise
  // the catalog price. Cents.
  _headlinePriceCents(variant) {
    const priceState = this._resolvePriceState(variant);
    if (priceState && priceState.memberActive) {
      // memberMajor is whole kroner (decimal) — same cents conversion as
      // card-price.liquid's server scaffold.
      return Math.round(priceState.memberMajor * 100);
    }
    return variant.price;
  }

  // Keeps the size row's current-product card on the selected variant's price,
  // matching the PDP price block. The configurator isn't swapped on variant
  // change, so the server-rendered value would otherwise go stale.
  _updateCurrentSizePrice() {
    const priceEl = this.querySelector('[data-configurator-current-price]');
    if (!priceEl || typeof window.formatMoney !== 'function') return;
    const variant = this._findVariantById(this.currentVariantId);
    // Missing catalog data — keep the server-rendered value.
    if (!variant || !variant.price) return;
    priceEl.textContent = window.formatMoney(this._headlinePriceCents(variant) / 100);
  }

  // ─── Cart integration ──────────────────────────────────────────────────────

  // Returns the extra line items (selected accessory slots + upsells) to add
  // alongside the main product variant, for callers building a multi-item cart/add request.
  getCartItems() {
    const items = [];
    Object.values(this.selectedAddons).forEach((addon) => {
      if (addon) items.push({ id: parseInt(addon.variantId, 10), quantity: 1 });
    });
    this.selectedUpsells.forEach((upsell) => {
      items.push({ id: parseInt(upsell.variantId, 10), quantity: 1 });
    });
    return items;
  }

  // ─── Variant helpers ───────────────────────────────────────────────────────

  _findVariantById(id) {
    return this.variants.find((v) => v.id === id) || null;
  }

  _optionsFromVariant(variant) {
    if (!variant) return { option1: null, option2: null, option3: null };
    return { option1: variant.option1, option2: variant.option2, option3: variant.option3 };
  }

  _richInfoForValue(variant, value) {
    return variant?.richInfo?.find((r) => r.name === value) || null;
  }

  _findVariantByOptions(option1, option2, option3) {
    const matches = (v) => {
      const o1 = option1 == null || v.option1 === option1;
      const o2 = option2 == null || v.option2 === option2;
      const o3 = option3 == null || v.option3 === option3;
      return o1 && o2 && o3;
    };
    // Prefer a purchasable match. A partially-specified combination (a product
    // with no option 3, or an option value tested before the others resolve)
    // matches several variants, and returning a sold-out one would mark the
    // whole option unavailable while a purchasable equivalent exists.
    return this.variants.find((v) => matches(v) && v.available)
      || this.variants.find(matches)
      || null;
  }

  // ─── Option availability ──────────────────────────────────────────────────
  //
  // Two tiers, because "you can't buy this" and "you can't buy this *with your
  // current picks*" need different answers. The customer escapes the second by
  // changing another row, so collapsing them into one state would make a
  // reachable combination look permanently gone.
  //
  //   'available'   — the resolved variant exists and is purchasable
  //   'unavailable' — purchasable in another combination, but not this one
  //   'sold-out'    — no purchasable variant anywhere on this product
  //
  // Callers pass their own "sold out everywhere" test because the axis differs:
  // a plain option value scans variants directly, while a firmness side has to
  // scan every pairing with the opposite side.
  _stateFor(matchVariant, isSoldOutEverywhere) {
    if (matchVariant && matchVariant.available) return 'available';
    return isSoldOutEverywhere ? 'sold-out' : 'unavailable';
  }

  _valueSoldOutEverywhere(optionKey, value) {
    return !this.variants.some((v) => v[optionKey] === value && v.available);
  }

  // A side value is sold out only when NO pairing with the opposite side yields
  // a purchasable variant. Testing just the current pairing would strike out a
  // value the customer can still reach by changing the other side first.
  _firmnessSideSoldOut(side, sideValue) {
    const others = side === 'left' ? (this._rightSides || []) : (this._leftSides || []);
    return !others.some((other) => {
      const option2 = side === 'left'
        ? this._findOption2ForSides(sideValue, other)
        : this._findOption2ForSides(other, sideValue);
      if (!option2) return false;
      return this.variants.some((v) => v.option2 === option2 && v.available);
    });
  }

  _priceDelta(variantA, variantB) {
    if (!variantA || !variantB) return null;
    return variantA.price - variantB.price;
  }

  // Absolute value formatted through the shop money format, so these labels agree with the
  // Liquid-rendered ones (price-as-money). window.formatMoney drops a zero-cents group but
  // keeps a real one, which is the point: "700 kr", not "700,00 kr",
  // and still "700,50 kr" when there is genuinely half a krone. Callers add their own sign, so the
  // formatter never has to sign a negative. The toLocaleString line is the fallback for the window
  // in which main.js has not defined window.formatMoney yet.
  _formatAbs(cents) {
    const major = Math.abs(cents / 100);
    if (typeof window.formatMoney === 'function') return window.formatMoney(major);
    return `${major.toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr.`;
  }

  _formatMoney(cents) {
    return `${cents < 0 ? '-' : ''}${this._formatAbs(cents)}`;
  }

  _formatDelta(delta) {
    return `${delta < 0 ? '-' : '+'}${this._formatAbs(delta)}`;
  }

  // ─── Firmness — detect dual-side and build panels ─────────────────────────

  _parseFirmness(value) {
    // Split "Ekstra fastFast" → ["Ekstra fast", "Fast"] only at a camelCase boundary
    // (lowercase directly followed by uppercase, no space). This prevents natural
    // multi-word option values like "Natur Ubehandlet Eg" from being incorrectly split.
    const match = value.match(/^(.+[a-zæøå])([A-ZÆØÅ].*)$/);
    if (match) return [match[1].trim(), match[2].trim()];
    return [value, null];
  }

  // Returns the raw option2 string for a left+right side pair by scanning variant data.
  // Tries both orderings (left+right and right+left) since "FastMedium" and "Medium+Fast"
  // are the same variant — the UI left/right is a display preference, not a data distinction.
  // A null right-side from _parseFirmness means both sides are the same value.
  _findOption2ForSides(left, right) {
    const tryOrder = (a, b) => this.variants.find((v) => {
      const [l, r] = this._parseFirmness(v.option2 || '');
      return l === a && (r === b || (r === null && a === b));
    })?.option2;
    return tryOrder(left, right) ?? tryOrder(right, left) ?? null;
  }

  _isDualSided() {
    // Dual-side parsing only applies when option 2 actually is firmness — Liquid
    // flags the row with data-firmness-option. Other option-2 axes (e.g. Ben/Stel)
    // use the generic panel and must never have their values split on capitals.
    const row = this.querySelector('[data-configurator-section="firmness"]');
    if (!row || !row.hasAttribute('data-firmness-option')) return false;

    const option2Values = [...new Set(this.variants.map((v) => v.option2).filter(Boolean))];
    return option2Values.some((val) => {
      const [, right] = this._parseFirmness(val);
      return right !== null;
    });
  }

  _initFirmness() {
    const row = this.querySelector('[data-configurator-section="firmness"]');
    if (!row) return;

    const option2Values = [...new Set(this.variants.map((v) => v.option2).filter(Boolean))];
    const isDual = this._isDualSided();

    if (!isDual) return; // single-side: default header + panel used by _initVariantPanels

    // Replace single header with dual headers
    const header = row.querySelector('[data-configurator-toggle="firmness"]');
    const panel = row.querySelector('[data-configurator-panel="firmness"]');
    const label = row.querySelector('.configurator__row-label')?.textContent || 'Fasthed';

    const leftSides = [...new Set(option2Values.map((v) => this._parseFirmness(v)[0]))];
    const rightSides = [...new Set(option2Values.map((v) => this._parseFirmness(v)[1] || this._parseFirmness(v)[0]))];

    const parsed2 = this.selectedOptions.option2
      ? this._parseFirmness(this.selectedOptions.option2)
      : [null, null];
    const currentLeft = parsed2[0] || leftSides[0] || '';
    // Single-sided option2 (parsed2[1] === null) means both sides are the same value
    const currentRight = parsed2[1] || parsed2[0] || rightSides[0] || '';

    header.remove();
    panel.remove();

    row.innerHTML = `
      <div class="configurator__dual-header">
        <span class="configurator__row-label">${label}</span>
      </div>
      <div class="configurator__dual-sides">
        <button type="button" class="configurator__row-subheader" data-configurator-toggle="firmness-left" aria-expanded="false">
          ${this._firmnessSide1Label ? `<span class="configurator__row-subheader-label">${this._firmnessSide1Label}</span>` : ''}
          <span class="configurator__row-value" data-configurator-value="firmness-left">${currentLeft}</span>
          <span class="configurator__row-chevron-icon">${this._chevronSvg()}</span>
        </button>
        <div class="configurator__panel" data-configurator-panel="firmness-left" hidden></div>
        <button type="button" class="configurator__row-subheader" data-configurator-toggle="firmness-right" aria-expanded="false">
          ${this._firmnessSide2Label ? `<span class="configurator__row-subheader-label">${this._firmnessSide2Label}</span>` : ''}
          <span class="configurator__row-value" data-configurator-value="firmness-right">${currentRight}</span>
          <span class="configurator__row-chevron-icon">${this._chevronSvg()}</span>
        </button>
        <div class="configurator__panel" data-configurator-panel="firmness-right" hidden></div>
      </div>
    `;

    this._firmnessRow = row;
    this._leftSides = leftSides;
    this._rightSides = rightSides;
    this._selectedLeft = currentLeft;
    this._selectedRight = currentRight;

    this._renderFirmnessPanel(row.querySelector('[data-configurator-panel="firmness-left"]'), leftSides, 'left');
    this._renderFirmnessPanel(row.querySelector('[data-configurator-panel="firmness-right"]'), rightSides, 'right');

    // Single delegated listener on the persistent row element
    row.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-firmness-side]');
      if (!btn) return;
      this._selectFirmnessSide(btn.dataset.firmnessSide, btn.dataset.firmnessValue, row);
    });
  }

  _buildOptionCard(value, matchVariant, delta, isSelected, dataAttrs, state = 'available', hideZeroDelta = false) {
    const richInfo = this._richInfoForValue(matchVariant, value);
    const image = richInfo?.image || null;
    const desc = configuratorRenderRichText(richInfo?.description);
    const imageHtml = image
      ? `<img class="configurator__option-image" src="${image}" alt="${value}" width="72" height="72" loading="lazy">`
      : '';
    const attrs = Object.entries(dataAttrs).map(([k, v]) => `${k}="${v}"`).join(' ');

    // Only a combination with no variant behind it is hard-disabled — clicking
    // it genuinely cannot resolve to anything. A sold-out variant stays
    // clickable so selecting it lets the add-to-cart button explain itself,
    // matching how the theme's own variant picker marks rather than blocks.
    const isDisabled = matchVariant === null;
    const badge = state === 'sold-out'
      ? this._soldOutLabel
      : (state === 'unavailable' ? this._unavailableLabel : '');

    return `
      <button type="button" class="configurator__option${isSelected ? ' is-selected' : ''}${state !== 'available' ? ` is-${state}` : ''}" ${isDisabled ? 'disabled' : ''} ${attrs}>
        ${imageHtml}
        <div class="configurator__option-info">
          <span class="configurator__option-name">${value}</span>
          ${desc ? `<span class="configurator__option-desc">${desc}</span>` : ''}
          ${badge ? `<span class="configurator__option-badge">${badge}</span>` : ''}
          <span class="configurator__option-price">${delta !== null && !(hideZeroDelta && delta === 0) ? this._formatDelta(delta) : ''}</span>
        </div>
      </button>`;
  }

  _renderFirmnessPanel(panel, sides, side) {
    const currentVariant = this._findVariantById(this.currentVariantId);

    const rows = sides.map((sideValue) => {
      // Build the candidate pairing using the tracked display selection for the other side
      const left = side === 'left' ? sideValue : (this._selectedLeft || sideValue);
      const right = side === 'right' ? sideValue : (this._selectedRight || sideValue);
      const option2 = this._findOption2ForSides(left, right);

      const matchVariant = option2
        ? this._findVariantByOptions(this.selectedOptions.option1, option2, this.selectedOptions.option3)
        : null;
      return { sideValue, matchVariant, delta: this._priceDelta(matchVariant, currentVariant) };
    });

    // When every side option is priced the same, showing "+0 kr." on each one
    // adds noise instead of information — hide the price row entirely instead.
    const hideZeroDelta = rows.every(({ delta }) => delta === null || delta === 0);

    panel.innerHTML = rows.map(({ sideValue, matchVariant, delta }) => {
      const isSelected = side === 'left'
        ? this._selectedLeft === sideValue
        : this._selectedRight === sideValue;
      const state = this._stateFor(matchVariant, this._firmnessSideSoldOut(side, sideValue));

      return this._buildOptionCard(sideValue, matchVariant, delta, isSelected, {
        'data-firmness-side': side,
        'data-firmness-value': sideValue,
      }, state, hideZeroDelta);
    }).join('');
  }

  _selectFirmnessSide(side, value, row) {
    const newLeft = side === 'left' ? value : (this._selectedLeft || value);
    const newRight = side === 'right' ? value : (this._selectedRight || value);

    // _selectedLeft/_selectedRight track the UI display order independently of the
    // stored option2 string (e.g. left=Medium right=Fast also matches "FastMedium").
    const option2 = this._findOption2ForSides(newLeft, newRight);
    if (!option2) return;

    this._selectedLeft = newLeft;
    this._selectedRight = newRight;
    this.selectedOptions.option2 = option2;
    this._updateVariantFromOptions('option2');

    row.querySelectorAll(`[data-firmness-side="${side}"]`).forEach((btn) => {
      btn.classList.toggle('is-selected', btn.dataset.firmnessValue === value);
    });

    this._closePanel(row.querySelector(`[data-configurator-panel="firmness-${side}"]`),
      row.querySelector(`[data-configurator-toggle="firmness-${side}"]`));
  }

  // ─── Variant option panels (topmattress + single-side firmness) ────────────

  _initVariantPanels() {
    const sections = [
      { key: 'firmness', optionKey: 'option2' },
      { key: 'topmattress', optionKey: 'option3' },
    ];

    this._variantPanelSections = sections.filter(({ key }) => {
      if (key === 'firmness' && this._isDualSided()) return false;
      return !!this.querySelector(`[data-configurator-panel="${key}"]`);
    });

    this._variantPanelSections.forEach(({ key, optionKey }) => {
      const panel = this.querySelector(`[data-configurator-panel="${key}"]`);

      this._renderVariantPanel(panel, key, optionKey);

      // Listener registered once on the persistent panel element
      panel.addEventListener('click', (e) => {
        const btn = e.target.closest(`[data-variant-option="${key}"]`);
        if (!btn) return;
        const val = btn.dataset.optionValue;
        this.selectedOptions[optionKey] = val;
        this._updateVariantFromOptions(optionKey);

        this._closePanel(panel, this.querySelector(`[data-configurator-toggle="${key}"]`));
      });
    });
  }

  _renderVariantPanel(panel, key, optionKey) {
    const uniqueValues = [...new Set(this.variants.map((v) => v[optionKey]).filter(Boolean))];
    const currentVariant = this._findVariantById(this.currentVariantId);

    const rows = uniqueValues.map((val) => {
      const opts = { ...this.selectedOptions, [optionKey]: val };
      const matchVariant = this._findVariantByOptions(opts.option1, opts.option2, opts.option3);
      return { val, matchVariant, delta: this._priceDelta(matchVariant, currentVariant) };
    });

    // Single-side firmness gets the same "hide +0 kr. when every option is the
    // same price" treatment as the dual-sided panel (_renderFirmnessPanel);
    // topmattress keeps showing its price on every card.
    const hideZeroDelta = key === 'firmness' && rows.every(({ delta }) => delta === null || delta === 0);

    panel.innerHTML = rows.map(({ val, matchVariant, delta }) => {
      const isSelected = this.selectedOptions[optionKey] === val;
      const state = this._stateFor(matchVariant, this._valueSoldOutEverywhere(optionKey, val));

      return this._buildOptionCard(val, matchVariant, delta, isSelected, {
        'data-variant-option': key,
        'data-option-value': val,
      }, state, hideZeroDelta);
    }).join('');
  }

  _refreshVariantPanels() {
    this._variantPanelSections?.forEach(({ key, optionKey }) => {
      const panel = this.querySelector(`[data-configurator-panel="${key}"]`);
      if (panel) this._renderVariantPanel(panel, key, optionKey);
    });

    if (this._firmnessRow) {
      const leftPanel = this._firmnessRow.querySelector('[data-configurator-panel="firmness-left"]');
      const rightPanel = this._firmnessRow.querySelector('[data-configurator-panel="firmness-right"]');
      if (leftPanel) this._renderFirmnessPanel(leftPanel, this._leftSides, 'left');
      if (rightPanel) this._renderFirmnessPanel(rightPanel, this._rightSides, 'right');
    }
  }

  // ─── Accessory slot panels (exclusive-select with a "none" option) ────────
  //
  // Unlike the fixed option rows above, slot1/slot2 come from a variant-level
  // metafield (prod_config_rel_prod1/2 — a list of related product variants,
  // not a single product reference), so the row's label and its option list
  // can both differ per variant. The panel content is therefore fully
  // JS-rendered from the per-variant JSON (same approach as
  // _renderVariantPanel for firmness/topmattress) and rebuilt on every variant
  // change via _refreshAddonPanels, rather than server-rendered once.

  _initAddonPanels() {
    // "None" option copy comes from block settings, so it's passed in as data
    // attributes (JS has no access to the `t` filter or Liquid settings).
    this._addonNoneLabels = {
      slot1: { label: this.dataset.slot1NoneLabel || '', price: this.dataset.slot1NonePriceLabel || '' },
      slot2: { label: this.dataset.slot2NoneLabel || '', price: this.dataset.slot2NonePriceLabel || '' },
    };

    this._addonSections = ['slot1', 'slot2'].filter((key) => !!this.querySelector(`[data-configurator-panel="${key}"]`));

    this._addonSections.forEach((key) => {
      const panel = this.querySelector(`[data-configurator-panel="${key}"]`);

      this._renderAddonPanel(key);

      panel.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-addon-option]');
        if (!btn) return;

        const type = btn.dataset.addonType;
        const isNone = 'addonNone' in btn.dataset;

        panel.querySelectorAll(`[data-addon-type="${type}"]`).forEach((b) => b.classList.remove('is-selected'));

        if (isNone) {
          btn.classList.add('is-selected');
          this.selectedAddons[type] = null;
          this._addonMemory[type] = null;
        } else {
          const isAlreadySelected = this.selectedAddons[type]?.variantId === btn.dataset.addonVariantId;
          if (!isAlreadySelected) {
            btn.classList.add('is-selected');
            this.selectedAddons[type] = {
              variantId: btn.dataset.addonVariantId,
              price: parseInt(btn.dataset.addonPrice, 10),
              name: btn.querySelector('.configurator__option-name')?.textContent,
            };
            const variant = this._findVariantById(this.currentVariantId);
            const opt = (variant?.[type]?.options || []).find((o) => String(o.variantId) === btn.dataset.addonVariantId);
            this._addonMemory[type] = opt ? { ...opt } : { variantId: btn.dataset.addonVariantId };
          } else {
            // Deselected back to none — re-select the none button if present
            panel.querySelector(`[data-addon-none="${type}"]`)?.classList.add('is-selected');
            this.selectedAddons[type] = null;
            this._addonMemory[type] = null;
          }
        }

        this._updateAddonLabel(key);
        this._updateTotalPrice();
        this._dispatchSelectionChanged();
        this._closePanel(panel, this.querySelector(`[data-configurator-toggle="${key}"]`));
      });
    });

    this._refreshAddonsHeading();
  }

  // Rebuilds one slot's row label/value and panel options from the currently
  // selected variant's JSON data. Hides the whole row when that variant has
  // nothing in this slot, and drops a selection that no longer applies.
  _renderAddonPanel(key) {
    const row = this.querySelector(`[data-configurator-section="${key}"]`);
    const panel = this.querySelector(`[data-configurator-panel="${key}"]`);
    if (!row || !panel) return;

    const variant = this._findVariantById(this.currentVariantId);
    const slot = variant?.[key];
    const options = slot?.options || [];

    if (!options.length) {
      // Live selection goes, but _addonMemory is kept: this may just be a
      // variant whose slot data hasn't been fetched yet (see
      // _ensureVariantRichDataFetched), and the re-render after that fetch
      // should still be able to restore the shopper's pick.
      row.hidden = true;
      this.selectedAddons[key] = null;
      return;
    }
    row.hidden = false;

    const labelEl = row.querySelector('.configurator__row-label');
    if (labelEl) labelEl.textContent = slot.label || '';

    const selected = this.selectedAddons[key];
    const stillValid = selected && options.some((opt) => String(opt.variantId) === String(selected.variantId));
    if (!stillValid) this.selectedAddons[key] = this._rematchAddon(key, options);

    const none = this._addonNoneLabels[key];
    const isNoneSelected = !this.selectedAddons[key];
    panel.innerHTML = this._buildAddonNoneOption(key, none, isNoneSelected)
      + options.map((opt) => this._buildAddonOption(key, opt, !isNoneSelected && String(opt.variantId) === this.selectedAddons[key].variantId)).join('');

    this._updateAddonLabel(key);
  }

  // Carries an accessory pick across variant changes. Switching colour swaps
  // the whole slot list for colour-matched accessory variants with different
  // ids, so a plain variantId comparison always fails and the slot would
  // silently reset to "none". Instead, find the equivalent accessory in the
  // new list from what the shopper last picked: same variant, else same
  // accessory product (colour-matched variant of the same product), else same
  // title, else same position in the list. Null when nothing matches.
  _rematchAddon(key, options) {
    const remembered = this._addonMemory[key];
    if (!remembered) return null;

    const byVariant = options.find((o) => String(o.variantId) === String(remembered.variantId));
    const byProduct = remembered.productId
      ? options.find((o) => String(o.productId) === String(remembered.productId))
      : null;
    const byTitle = remembered.title
      ? options.find((o) => o.title === remembered.title)
      : null;
    const byIndex = Number.isInteger(remembered.index) ? options[remembered.index] : null;
    const match = byVariant || byProduct || byTitle || byIndex;
    if (!match) return null;

    return {
      variantId: String(match.variantId),
      price: match.price,
      name: match.title,
    };
  }

  _buildAddonNoneOption(key, none, isSelected) {
    return `
      <button
        type="button"
        class="configurator__option${isSelected ? ' is-selected' : ''}"
        data-addon-option
        data-addon-type="${key}"
        data-addon-none="${key}">
        <div class="configurator__option-info">
          <span class="configurator__option-name">${none.label}</span>
          <span class="configurator__option-price">${none.price}</span>
        </div>
      </button>`;
  }

  _buildAddonOption(key, opt, isSelected) {
    const priceText = typeof window.formatMoney === 'function' ? window.formatMoney(opt.price / 100) : opt.price;
    // Eager (not lazy) on purpose: this panel is rendered inside a hidden
    // row as soon as the color changes, and a lazy image inside a hidden
    // ancestor won't be fetched until the panel is opened. Loading eagerly
    // here means the addon images are already cached by the time the
    // shopper opens the dropdown.
    const imageHtml = opt.image
      ? `<img class="configurator__option-image" src="${opt.image}" alt="${opt.imageAlt || opt.title}" width="72" height="72">`
      : '';
    return `
      <button
        type="button"
        class="configurator__option${isSelected ? ' is-selected' : ''}"
        data-addon-option
        data-addon-type="${key}"
        data-addon-variant-id="${opt.variantId}"
        data-addon-price="${opt.price}">
        ${imageHtml}
        <div class="configurator__option-info">
          <span class="configurator__option-name">${opt.title}</span>
          <span class="configurator__option-price">+ ${priceText}</span>
        </div>
      </button>`;
  }

  _refreshAddonPanels() {
    this._addonSections?.forEach((key) => this._renderAddonPanel(key));
    this._refreshAddonsHeading();
  }

  // The shared "Tilbehør" heading sits above slot1/slot2/upsell — keep it in
  // sync as slot rows hide/show per variant so it never floats above nothing.
  _refreshAddonsHeading() {
    const heading = this.querySelector('[data-configurator-addons-heading]');
    if (!heading) return;

    const anySlotVisible = this._addonSections?.some((key) => {
      const row = this.querySelector(`[data-configurator-section="${key}"]`);
      return row && !row.hidden;
    });
    const hasUpsell = !!this.querySelector('[data-configurator-section="upsell"]');
    heading.hidden = !(anySlotVisible || hasUpsell);
  }

  _updateAddonLabel(key) {
    const valueEl = this.querySelector(`[data-configurator-value="${key}"]`);
    if (!valueEl) return;

    const noneBtn = this.querySelector(`[data-addon-none="${key}"]`);
    valueEl.textContent = this.selectedAddons[key]
      ? this.selectedAddons[key].name
      : (noneBtn?.querySelector('.configurator__option-name')?.textContent || '');
  }

  // ─── Upsell panel (independent checkbox-style toggles, all selectable at once) ──

  _initUpsellPanel() {
    const panel = this.querySelector('[data-configurator-panel="upsell"]');
    if (!panel) return;

    panel.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-upsell-option]');
      if (!btn) return;

      const variantId = btn.dataset.upsellVariantId;
      if (this.selectedUpsells.has(variantId)) {
        this.selectedUpsells.delete(variantId);
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-pressed', 'false');
      } else {
        this.selectedUpsells.set(variantId, {
          variantId,
          price: parseInt(btn.dataset.upsellPrice, 10),
          name: btn.querySelector('.configurator__option-name')?.textContent,
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
      }

      this._updateUpsellLabel();
      this._updateTotalPrice();
      this._dispatchSelectionChanged();
    });
  }

  // Notifies listeners (e.g. product-form.js) that the addon/upsell selection
  // changed, so a stale "item unavailable" cart error can be cleared as soon
  // as the customer picks something else, rather than only on next submit.
  _dispatchSelectionChanged() {
    this.dispatchEvent(new CustomEvent('configurator:selection-changed', {
      bubbles: true,
      detail: {
        selectedAddons: this.selectedAddons,
        selectedUpsells: [...this.selectedUpsells.values()],
      },
    }));
  }

  _updateUpsellLabel() {
    const valueEl = this.querySelector('[data-configurator-value="upsell"]');
    if (!valueEl) return;

    if (this.selectedUpsells.size) {
      valueEl.textContent = [...this.selectedUpsells.values()].map((u) => u.name).join(', ');
    } else {
      valueEl.textContent = this.querySelector('[data-configurator-section="upsell"] .configurator__row-label')?.textContent || '';
    }
  }

  // ─── Size (navigate to different product) ─────────────────────────────────

  _initSizeOptions() {
    this.querySelector('[data-configurator-section="size"]')?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-size-option]');
      if (!btn || !btn.dataset.productUrl) return;
      window.location.href = btn.dataset.productUrl;
    });
  }

  // ─── Accordion toggles ────────────────────────────────────────────────────

  _initToggles() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-configurator-toggle]');
      if (!btn) return;
      const key = btn.dataset.configuratorToggle;
      const panel = this.querySelector(`[data-configurator-panel="${key}"]`);
      if (!panel) return;

      const isOpen = !panel.hidden;
      if (isOpen) {
        this._closePanel(panel, btn);
      } else {
        // Close all other open panels (skip panels without a toggle key — they're always-open)
        this.querySelectorAll('.configurator__panel[data-configurator-panel]:not([hidden])').forEach((p) => {
          const toggleKey = p.dataset.configuratorPanel;
          this._closePanel(p, this.querySelector(`[data-configurator-toggle="${toggleKey}"]`));
        });
        this._openPanel(panel, btn);
      }
    });
  }

  // Animates `panel` to `targetHeight` (px) and invokes `onDone` once the
  // transition completes. Cancels any in-flight animation on the same panel
  // first so rapid toggling can't leak listeners or double-fire callbacks.
  _animatePanelHeight(panel, targetHeight, onDone) {
    if (panel._panelTransitionEnd) {
      panel.removeEventListener('transitionend', panel._panelTransitionEnd);
    }
    const onEnd = (e) => {
      if (e.target !== panel || e.propertyName !== 'max-height') return;
      panel.removeEventListener('transitionend', onEnd);
      panel._panelTransitionEnd = null;
      onDone();
    };
    panel._panelTransitionEnd = onEnd;
    panel.addEventListener('transitionend', onEnd);
    panel.style.maxHeight = `${targetHeight}px`;
  }

  _openPanel(panel, btn) {
    if (!panel || !panel.hidden) return;
    btn?.setAttribute('aria-expanded', 'true');
    btn?.closest('.configurator__row')?.classList.add('is-open');

    panel.hidden = false;
    panel.style.maxHeight = '0px';
    void panel.offsetHeight; // force reflow so the 0px height is committed before animating
    this._animatePanelHeight(panel, panel.scrollHeight, () => {
      panel.style.maxHeight = '';
    });
  }

  _closePanel(panel, btn) {
    if (!panel || panel.hidden) return;
    btn?.setAttribute('aria-expanded', 'false');
    btn?.closest('.configurator__row')?.classList.remove('is-open');

    panel.style.maxHeight = `${panel.scrollHeight}px`;
    void panel.offsetHeight; // force reflow so the current height is committed before animating to 0
    this._animatePanelHeight(panel, 0, () => {
      panel.hidden = true;
      panel.style.maxHeight = '';
    });
  }

  // ─── Variant update ───────────────────────────────────────────────────────

  // Mirrors the standard variant picker's correction (assets/variant-picker.js
  // correctIncompatibleOptions): if the option the customer just picked has no
  // purchasable variant together with the current picks on the other axes,
  // keep that pick fixed and jump to the first available variant that still
  // has it, correcting the other axes to match — instead of leaving the
  // customer on a sold-out/nonexistent combination (e.g. a color that isn't
  // in stock with the currently selected legs/firmness).
  // Returns whether option2 or option3 actually changed as a result — that's
  // the signal callers need to decide whether the fabric grid (which is keyed
  // off those two) requires a re-render.
  _correctIncompatibleOptions(changedKey) {
    const { option1, option2, option3 } = this.selectedOptions;
    const current = this._findVariantByOptions(option1, option2, option3);
    if (current && current.available) return false;

    const changedValue = this.selectedOptions[changedKey];
    const alternative = this.variants.find((v) => v[changedKey] === changedValue && v.available);
    if (!alternative) return false; // nothing available has this value at all

    this.selectedOptions.option1 = alternative.option1;
    this.selectedOptions.option2 = alternative.option2;
    this.selectedOptions.option3 = alternative.option3;

    // The dual-side firmness UI tracks its own left/right split independently
    // of the option2 string it resolves to (e.g. left=Medium/right=Fast and
    // left=Fast/right=Medium both resolve to the same option2 pairing) — only
    // re-derive it when option2 itself actually changed. Re-parsing on every
    // correction would force _parseFirmness's fixed left-then-right order
    // even when the customer had the same pairing displayed the other way
    // around, silently swapping the two sides.
    if (this._firmnessRow && alternative.option2 !== option2) {
      const [left, right] = this._parseFirmness(alternative.option2 || '');
      this._selectedLeft = left;
      this._selectedRight = right || left;
    }

    return alternative.option2 !== option2 || alternative.option3 !== option3;
  }

  // Keeps every row's closed-state value text in sync with selectedOptions —
  // needed because _correctIncompatibleOptions can change a different option
  // than the one the customer actually clicked.
  _syncOptionValueLabels() {
    const { option1, option2, option3 } = this.selectedOptions;

    const fabricEl = this.querySelector('[data-configurator-value="fabric"]');
    if (fabricEl && option1 != null) fabricEl.textContent = option1;

    if (this._firmnessRow) {
      const leftEl = this._firmnessRow.querySelector('[data-configurator-value="firmness-left"]');
      const rightEl = this._firmnessRow.querySelector('[data-configurator-value="firmness-right"]');
      if (leftEl) leftEl.textContent = this._selectedLeft;
      if (rightEl) rightEl.textContent = this._selectedRight;
    } else {
      const firmnessEl = this.querySelector('[data-configurator-value="firmness"]');
      if (firmnessEl && option2 != null) firmnessEl.textContent = option2;
    }

    const topmattressEl = this.querySelector('[data-configurator-value="topmattress"]');
    if (topmattressEl && option3 != null) topmattressEl.textContent = option3;
  }

  // changedKey identifies which option (option1/2/3) the customer just picked,
  // so an incompatible combination can be corrected around that fixed value.
  // Omitted on the initial render, when there's nothing to correct yet.
  _updateVariantFromOptions(changedKey = null) {
    const corrected = changedKey ? this._correctIncompatibleOptions(changedKey) : false;

    const { option1, option2, option3 } = this.selectedOptions;
    const variant = this._findVariantByOptions(option1, option2, option3);

    // Update currentVariantId before refreshing panels so price deltas are
    // always calculated against the newly selected variant as the baseline.
    if (variant) this.currentVariantId = variant.id;

    // Always refresh panels so is-selected stays in sync with selectedOptions,
    // regardless of whether a matching variant was found.
    this._syncOptionValueLabels();
    this._refreshVariantPanels();
    this._refreshAddonPanels();
    this._updateTotalPrice();
    this._updateCurrentSizePrice();
    this._dispatchSelectionChanged();

    // The fabric grid's per-swatch prices are relative to currentVariantId,
    // which changes on every selection (not just a corrected one), so it
    // needs a re-render every time the modal is open — _refreshFabricGrid
    // (not the raw innerHTML render) keeps that from stealing focus out of
    // the dialog on every tap. Stock and campaigns, unlike price, are keyed
    // to the combo rather than the fabric, so they're only reloaded when a
    // correction actually changed option2/option3 — the old snapshot then
    // no longer covers the variants the grid reads from.
    if (this._modal && !this._modal.hidden) {
      this._refreshFabricGrid();
      if (corrected) {
        this._loadWarehouseStock();
        this._loadComboCampaigns(this._gridVariantsForCurrentOptions());
      }
    }

    if (!variant) return;

    // Rich data (richInfo + slot1/slot2) is only pre-fetched for one variant
    // per distinct fabric at page load, to keep the query bounded — so
    // switching firmness/mattress without changing fabric can land on a
    // variant whose own slot1/slot2 were never fetched, even though the
    // fabric's representative variant was. Backfilling only covers
    // richInfo (fabric-level, safe to share); slots can legitimately differ
    // per firmness/mattress, so this variant's own data is fetched on demand
    // instead — mirrors the lazy campaign fetch in _loadComboCampaigns.
    if (!variant.richFetched) this._ensureVariantRichDataFetched(variant);

    // Update the main product form's variant input
    const formInput = document.querySelector('.js-product-form-main input[name="id"]')
      || document.querySelector(`form[id="${this.dataset.productFormId}"] input[name="id"]`);
    if (formInput && formInput.value !== String(variant.id)) {
      formInput.value = variant.id;
      formInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.dispatchEvent(new CustomEvent('configurator:variant-changed', {
      bubbles: true,
      detail: {
        variant,
        selectedAddons: this.selectedAddons,
        selectedUpsells: [...this.selectedUpsells.values()],
      },
    }));

    this.dispatchEvent(new CustomEvent('on:variant:change', {
      bubbles: true,
      detail: {
        variant: {
          ...variant,
          featured_media: variant.featuredMediaId ? { id: variant.featuredMediaId } : null,
        },
        selectedOptions: [this.selectedOptions.option1, this.selectedOptions.option2, this.selectedOptions.option3],
      },
    }));

    this._updateUrl(variant.id);
    this._updateDynamicContent(variant.id);
  }

  // Mirrors variant-picker.js's updateUrl() — same `?variant=<id>` replaceState
  // so the address bar and reloads track whichever variant is selected here too.
  _updateUrl(variantId) {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;
    window.history.replaceState({}, '', `${productUrl}?variant=${variantId}`);
  }

  _updateDynamicContent(variantId) {
    // Other variant-dependent sections on the page (e.g. a spec table keyed to
    // the selected variant's metafields) need re-rendering too, exactly like
    // variant-picker.js's constructVariantUrl — otherwise they'd keep showing
    // whichever variant was selected on initial page load.
    const sectionIds = [this.dataset.sectionId];
    document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((el) => {
      if (el.closest('.rte')) return;
      const id = el.dataset.sectionId;
      if (id && !sectionIds.includes(id)) sectionIds.push(id);
    });

    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('sections', sectionIds.join(','));

    this._contentRequestId = (this._contentRequestId || 0) + 1;
    const requestId = this._contentRequestId;

    const doFetch = (u) => (typeof theme !== 'undefined' && theme.fetchCache)
      ? theme.fetchCache.fetch(u)
      : fetch(u).then((r) => r.text());

    doFetch(url.toString()).then((responseText) => {
      if (requestId !== this._contentRequestId) return;
      const data = JSON.parse(responseText);

      const newDocs = sectionIds
        .map((id) => data[id])
        .filter(Boolean)
        .map((html) => new DOMParser().parseFromString(html, 'text/html'));
      if (!newDocs.length) return;

      document.querySelectorAll('[data-dynamic-variant-content]').forEach((el) => {
        if (el.contains(this)) return; // never replace a container that holds the configurator
        const key = el.dataset.dynamicVariantContent;
        let replacement = null;
        for (const newDoc of newDocs) {
          replacement = newDoc.querySelector(`[data-dynamic-variant-content="${CSS.escape(key)}"]`);
          if (replacement) break;
        }
        if (!replacement) return;

        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: { toReplace: el, replaceWith: replacement }
        }));

        if (el.matches('input[name="id"]')) {
          // Preserve the node (and any listeners bound to it directly) —
          // mirrors variant-picker.js's same special case.
          [...replacement.attributes].forEach((attr) => el.setAttribute(attr.name, attr.value));
          el.value = replacement.value;
        } else {
          // A full node swap, not an innerHTML patch — custom elements in here
          // (e.g. <faq-ilva-tabs>) bind their listeners once in
          // connectedCallback, so an innerHTML patch would leave freshly
          // inserted children with no click handlers. replaceWith disconnects
          // the old node and connects the new one, re-running that callback.
          el.replaceWith(replacement);
        }
      });

    }).catch(() => {});
  }

  // ─── Fabric/color modal ───────────────────────────────────────────────────

  _initModal() {
    const sectionId = this.dataset.sectionId;
    const modal = document.getElementById(`configurator-fabric-modal-${sectionId}`);
    if (!modal) return;
    // Move to <body> to escape any stacking context created by the carousel's transform
    document.body.appendChild(modal);
    this._modal = modal;

    // Open button inside this element
    this.querySelector('[data-configurator-open-modal]')?.addEventListener('click', () => {
      this._openFabricModal();
    });

    // Close buttons (backdrop + buttons inside modal)
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-configurator-close-modal]')) {
        this._closeFabricModal();
      }
      if (e.target === modal) this._closeFabricModal();
    });

    // Confirm selection from list view
    modal.querySelector('[data-fabric-confirm]')?.addEventListener('click', () => {
      if (this.pendingFabric) {
        this._applyFabricSelection(this.pendingFabric);
      }
      this._closeFabricModal();
    });

    // Detail popup close — returns to the list, selection untouched
    modal.querySelectorAll('[data-fabric-detail-back]').forEach((el) => {
      el.addEventListener('click', () => {
        this._showFabricView('list');
        modal.querySelector('[data-fabric-modal-view="list"] [data-configurator-close-modal]')?.focus();
      });
    });

    // Escape steps back one level (detail → list, list → closed); Tab cycles
    // within the modal so background content is unreachable while it's open.
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const detailView = modal.querySelector('[data-fabric-modal-view="detail"]');
        if (detailView && !detailView.hidden) {
          this._showFabricView('list');
          modal.querySelector('[data-fabric-modal-view="list"] [data-configurator-close-modal]')?.focus();
        } else {
          this._closeFabricModal();
        }
        return;
      }
      if (e.key === 'Tab') {
        // offsetParent is null inside the [hidden] view, so this only picks up
        // controls of whichever view is currently shown.
        const focusables = [...modal.querySelectorAll('button, select, input, a[href]')]
          .filter((el) => el.offsetParent !== null);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Filter changes
    modal.querySelectorAll('[data-fabric-filter]').forEach((el) => {
      el.addEventListener('change', () => this._renderFabricGrid());
    });

    this._buildFilterOptions();
    this._renderFabricGrid();
  }

  // The set of real combination variants — one per distinct fabric, paired
  // with whatever firmness/mattress is currently selected — that
  // _loadWarehouseStock needs. Small (bounded by distinct fabric count)
  // regardless of total variant count.
  _comboVariantsForCurrentOptions() {
    return this.fabricData
      .map((item) => this._findVariantByOptions(item.option1, this.selectedOptions.option2, this.selectedOptions.option3))
      .filter(Boolean);
  }

  // The variant a fabric card reads its price and badges from. Color comes
  // first: a fabric is offered as long as any variant in it is purchasable,
  // and picking one that doesn't exist with the current legs/firmness makes
  // _correctIncompatibleOptions move the other axes to the first available
  // variant in that color — so that landing variant, not a combo the
  // customer would never end up on, is what the card should describe.
  _gridVariantFor(item) {
    const comboVariant = this._findVariantByOptions(item.option1, this.selectedOptions.option2, this.selectedOptions.option3);
    if (comboVariant && comboVariant.available) return comboVariant;
    return this.variants.find((v) => v.option1 === item.option1 && v.available)
      || comboVariant
      || this._findVariantById(item.variantId);
  }

  // Everything the grid will read campaigns from — landing variants included,
  // which the combo set alone misses whenever a fabric isn't purchasable with
  // the current picks. Kept in step with the cards by sharing _gridVariantFor.
  _gridVariantsForCurrentOptions() {
    const seen = new Set();
    return this.fabricData
      .map((item) => this._gridVariantFor(item))
      .filter((v) => v && !seen.has(v.id) && seen.add(v.id));
  }

  _openFabricModal() {
    if (!this._modal) return;
    this.pendingFabric = this.selectedOptions.option1;
    this._showFabricView('list');
    this._renderFabricGrid();
    this._updateFabricHero(this.pendingFabric);
    this._loadWarehouseStock();
    this._loadComboCampaigns(this._gridVariantsForCurrentOptions());
    this._modal.hidden = false;
    document.body.classList.add('overflow-hidden');
    // Scope to the list view: an unscoped [data-configurator-close-modal] matches
    // the backdrop div first, and focusing a non-focusable element is a silent
    // no-op — focus would stay on the opener outside the modal, so the keydown
    // handler below never receives Escape or Tab and the trap never engages.
    this._modal.querySelector('[data-fabric-modal-view="list"] [data-configurator-close-modal]')?.focus();
  }

  _closeFabricModal() {
    if (!this._modal) return;
    this._modal.hidden = true;
    this.pendingFabric = null;
    document.body.classList.remove('overflow-hidden');
    // The opener lives inside this element and survives re-renders — safe to re-query
    this.querySelector('[data-configurator-open-modal]')?.focus();
  }

  _showFabricView(view) {
    this._modal.querySelectorAll('[data-fabric-modal-view]').forEach((el) => {
      el.hidden = el.dataset.fabricModalView !== view;
    });
  }

  // Identifies which firmness/top-mattress combo a _warehouseStock snapshot
  // belongs to — option1 (the fabric) doesn't matter here, since one fetch
  // covers every fabric's combo variant for the same option2/option3.
  _comboKey() {
    return `${this.selectedOptions.option2}||${this.selectedOptions.option3}`;
  }

  // Fetches, in one batched Storefront API call, whether each fabric's real
  // combination variant (this option1 + the currently selected firmness/top
  // mattress) is in stock at the central warehouse — judged by the same
  // deliveryWarehouseInStock rule as the PDP delivery estimate (store-locator.js),
  // so a swatch can't be called short delivery while the delivery line on the same
  // page says otherwise. That rule reads the variant's Centrallager flag, with every
  // shop's quantity against the sellable figure as the fallback, all from ONE
  // response: v.inventoryQuantity is a page-load snapshot and would drift from a
  // later shop read (ClickUp 869f86cbr, 869fbkn3h).
  // Re-renders the grid when it resolves so the short-delivery checkbox reflects
  // real data.
  async _loadWarehouseStock() {
    // Captured up front: a correction can change option2/option3 again while
    // this fetch is in flight, and the result being applied must be tagged
    // with the combo it actually answers for, not whatever is selected once
    // the await resolves.
    const comboKey = this._comboKey();

    if (!this._shopDomain || !this._storefrontToken || typeof window.fetchVariantsAvailabilityCached !== 'function') {
      return;
    }

    const comboVariants = this._comboVariantsForCurrentOptions().filter((v) => v.inventoryManaged);
    if (!comboVariants.length) return;

    // The await below is also what keeps this method finishing in a later task than
    // its callers: _openFabricModal and the post-correction branch in
    // _updateVariantFromOptions both call it mid-render, and it ends by re-rendering
    // the grid via _refreshFabricGrid, which would drop focus out of the dialog if it
    // ran inside the caller's own render.
    const gids = comboVariants.map((v) => `gid://shopify/ProductVariant/${v.id}`);
    let verdicts;
    try {
      verdicts = await window.fetchVariantsAvailabilityCached(this._shopDomain, this._storefrontToken, gids, this._warehouseAxId);
    } catch {
      // No data is not a "not short delivery" verdict. Leaving the map unwritten keeps
      // the grid in its pending flat list (see stockIsFresh in _renderFabricGrid).
      return;
    }

    // The modal may have been closed (or reopened for a different combo) by
    // the time this resolves.
    if (!this._modal || this._modal.hidden) return;

    // A later correction can have started its own fetch for a newer combo,
    // which may resolve before this one does — applying this result then
    // would clobber that fresher data with a stale snapshot for a combo
    // that's no longer selected. Only apply it if it still matches.
    if (comboKey !== this._comboKey()) return;

    const stock = new Map();
    comboVariants.forEach((v) => {
      stock.set(v.id, verdicts.get(`gid://shopify/ProductVariant/${v.id}`) === true);
    });
    this._warehouseStock = stock;
    this._warehouseStockComboKey = comboKey;
    this._refreshFabricGrid();
  }

  // _renderFabricGrid replaces the grid via innerHTML, which drops focus (and
  // breaks the modal's Tab/Escape trap) if the focused control was inside it
  // — e.g. re-rendering right after a swatch tap that triggered a correction.
  // Re-focuses the equivalent swatch afterward, falling back to the first
  // focusable one if the previously focused fabric is gone or sold out, and
  // to the modal's own close button if every swatch turned out to be
  // sold-out — a disabled [data-fabric-select] can't actually take focus, so
  // .focus() on one would otherwise be a silent no-op that leaves focus
  // outside the dialog.
  _refreshFabricGrid() {
    const grid = this._modal?.querySelector('[data-fabric-grid]');
    if (!grid) return;

    const hadFocus = grid.contains(document.activeElement);
    const focusedFabric = hadFocus
      ? document.activeElement.closest('[data-fabric-card]')?.dataset.fabricCard
      : null;

    this._renderFabricGrid();
    if (!hadFocus) return;

    const focusableSelect = (card) => {
      const btn = card.querySelector('[data-fabric-select]');
      return btn && !btn.disabled ? btn : null;
    };

    const cards = [...grid.querySelectorAll('[data-fabric-card]')];
    const preferred = cards.find((c) => c.dataset.fabricCard === focusedFabric);
    const target = (preferred && focusableSelect(preferred))
      || cards.map(focusableSelect).find(Boolean)
      || this._modal.querySelector('[data-fabric-modal-view="list"] [data-configurator-close-modal]');
    target?.focus();
  }

  _buildFilterOptions() {
    const colorSelect = this._modal.querySelector('[data-fabric-filter="color"]');
    const materialSelect = this._modal.querySelector('[data-fabric-filter="material"]');

    const colors = new Set();
    const materials = new Set();

    this.fabricData.forEach((item) => {
      item.richInfo.forEach((info) => {
        (Array.isArray(info.baseColor) ? info.baseColor : [info.baseColor]).forEach((c) => {
          if (typeof c === 'string' && c) colors.add(c);
        });
        (Array.isArray(info.material) ? info.material : [info.material]).forEach((m) => {
          if (typeof m === 'string' && m) materials.add(m);
        });
      });
    });

    [...colors].sort((a, b) => a.localeCompare(b)).forEach((c) => {
      const opt = document.createElement('option');
      opt.value = c;
      opt.textContent = c;
      colorSelect?.appendChild(opt);
    });

    [...materials].sort((a, b) => a.localeCompare(b)).forEach((m) => {
      const opt = document.createElement('option');
      opt.value = m;
      opt.textContent = m;
      materialSelect?.appendChild(opt);
    });
  }

  _renderFabricGrid() {
    const grid = this._modal.querySelector('[data-fabric-grid]');
    if (!grid) return;

    const filterColor = this._modal.querySelector('[data-fabric-filter="color"]')?.value || '';
    const filterMaterial = this._modal.querySelector('[data-fabric-filter="material"]')?.value || '';
    const filterShortDelivery = this._modal.querySelector('[data-fabric-filter="short-delivery"]')?.checked || false;
    // A correction (see _correctIncompatibleOptions) can change option2/
    // option3 while a stock fetch for the previous combo is still in
    // flight. Grouping against those stale ids would wrongly mark every
    // swatch as out of warehouse stock, so the checkbox is treated as
    // pending — flat list, no grouping — until _loadWarehouseStock lands
    // fresh data for the combo that's actually selected now.
    const stockIsFresh = this._warehouseStockComboKey === this._comboKey();

    const filtered = this.fabricData.filter((item) => {
      if (!item.richInfo.length) return true;
      const info = item.richInfo[0];
      if (filterColor) {
        const colors = Array.isArray(info.baseColor) ? info.baseColor : [info.baseColor];
        if (!colors.includes(filterColor)) return false;
      }
      if (filterMaterial) {
        const materials = Array.isArray(info.material) ? info.material : [info.material];
        if (!materials.includes(filterMaterial)) return false;
      }
      return true;
    });

    // Short delivery = in stock at the central warehouse for the real
    // combination variant (this fabric + current firmness/top mattress),
    // not the fabric's representative variant — same reasoning as the
    // comboVariant lookup below for availability state.
    const inWarehouseStock = (item) => {
      const comboVariant = this._findVariantByOptions(item.option1, this.selectedOptions.option2, this.selectedOptions.option3);
      return comboVariant ? this._warehouseStock.get(comboVariant.id) : false;
    };

    const cardHtml = (item) => {
      const info = item.richInfo[0] || {};
      const isPending = item.option1 === this.pendingFabric;

      // Price/badges come from the variant the customer would actually land
      // on (see _gridVariantFor), and campaigns are fetched for exactly that
      // set in _loadComboCampaigns — the two must stay in step.
      const variant = this._gridVariantFor(item);
      const currentVariant = this._findVariantById(this.currentVariantId);
      const delta = this._priceDelta(variant, currentVariant);
      const state = this._valueSoldOutEverywhere('option1', item.option1) ? 'sold-out' : 'available';

      const name = info.name || item.option1;
      const material = Array.isArray(info.material) ? info.material.join(', ') : info.material;

      // Campaign badges + member/sale pricing resolve from the same
      // price-schedule feed as product cards and the PDP, against the real
      // combination variant so the grid never shows a price the customer
      // won't get. Falls back to the metaobject badge when there's no feed.
      const priceState = this._resolvePriceState(variant);

      const badgeHtml = this._badgesHtml(priceState, info);

      // A sold-out card keeps its info button reachable — the badge carries
      // the state; the click guard below is what makes the tile inert.
      const stateHtml = state === 'sold-out'
        ? `<span class="configurator__fabric-badge configurator__fabric-badge--sold-out">${this._soldOutLabel}</span>`
        : '';

      const priceHtml = this._priceHtml(variant, delta);

      const deliveryHtml = info.deliveryNote
        ? `<span class="configurator__fabric-delivery">${info.deliveryNote}</span>`
        : '';

      const swatchSrc = info.image || '';

      // The select button wraps only the swatch image (a nested info button
      // would be invalid HTML); the card-level click handler keeps the whole
      // tile tappable for pointer users.
      return `
        <div class="configurator__fabric-card${isPending ? ' is-selected' : ''}${state !== 'available' ? ` is-${state}` : ''}"
          data-fabric-card="${item.option1}">
          <button type="button" class="configurator__fabric-select" data-fabric-select aria-pressed="${isPending}"${state === 'sold-out' ? ' disabled' : ''}>
            <span class="configurator__fabric-media">
              ${badgeHtml}
              ${stateHtml}
              ${swatchSrc ? `<img class="configurator__fabric-swatch" src="${swatchSrc}" alt="" loading="lazy">` : ''}
            </span>
            <span class="visually-hidden">${name}</span>
          </button>
          <div class="configurator__fabric-card-footer">
            <div class="configurator__fabric-name-row">
              <span class="configurator__fabric-name">${name}</span>
              <button type="button" class="configurator__fabric-info-btn" data-fabric-info="${item.option1}" aria-label="Se detaljer for ${name}">
                ${this._infoIconSvg()}
              </button>
            </div>
            ${material ? `<span class="configurator__fabric-material">${material}</span>` : ''}
            ${priceHtml}
            ${deliveryHtml}
          </div>
        </div>`;
    };

    const noResultsHtml = `<p class="configurator__fabric-empty">${this._fabricNoResultsLabel}</p>`;

    // The color/material filters alone can leave nothing to show, regardless
    // of the short-delivery checkbox — that's a genuine empty state.
    if (!filtered.length) {
      grid.innerHTML = noResultsHtml;
    } else if (filterShortDelivery && stockIsFresh) {
      // With the checkbox on, fabrics without warehouse stock stay visible
      // rather than being dropped from the grid — they're grouped below a
      // divider with a heading explaining why, so toggling the checkbox
      // never hides a fabric outright. If the filters above leave nothing
      // with short-delivery stock, the empty message takes that top slot
      // instead, with the rest still listed below it.
      const available = filtered.filter(inWarehouseStock);
      const unavailable = filtered.filter((item) => !inWarehouseStock(item));
      const availableHtml = available.length ? available.map(cardHtml).join('') : noResultsHtml;
      const dividerHtml = unavailable.length
        ? `<div class="configurator__fabric-divider">${this._shortDeliveryUnavailableLabel}</div>`
        : '';
      grid.innerHTML = availableHtml + dividerHtml + unavailable.map(cardHtml).join('');
    } else {
      grid.innerHTML = filtered.map(cardHtml).join('');
    }

    // Card selection — whole tile is tappable; the info button opts out.
    // A tap is a real selection, not just a pending pick — it applies the
    // variant (and its gallery image) immediately, same as confirming.
    grid.querySelectorAll('[data-fabric-card]').forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-fabric-info]')) return;
        // A sold-out fabric has no purchasable variant in any combination, so the
        // tile is inert. This guard — not the button's `disabled` — is what blocks
        // it: the listener sits on the card, so a click on the name, material or
        // price in the footer would otherwise still select it.
        if (card.classList.contains('is-sold-out')) return;
        const val = card.dataset.fabricCard;
        this.pendingFabric = val;
        grid.querySelectorAll('[data-fabric-card]').forEach((c) => {
          const selected = c.dataset.fabricCard === val;
          c.classList.toggle('is-selected', selected);
          c.querySelector('[data-fabric-select]')?.setAttribute('aria-pressed', String(selected));
        });
        this._updateFabricHero(val);
        this._applyFabricSelection(val);
      });
    });

    // Info button → detail popup (selection untouched — the popup has no confirm)
    grid.querySelectorAll('[data-fabric-info]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._openFabricDetail(btn.dataset.fabricInfo);
      });
    });
  }

  // ─── Fabric card pricing (shared price-schedule feed) ─────────────────────

  // Resolves one variant's price story through the shared resolver in main.js
  // — the single source of the Member > Sale > Regular precedence. Returns null
  // when the feed is absent (no schedule on the product, member prices switched
  // off, or main.js not yet upgraded), which keeps the delta-price fallback.
  _resolvePriceState(variant) {
    if (!variant || typeof window.resolveScheduleState !== 'function') return null;
    if (!variant.schedule && !variant.campaigns) return null;
    return window.resolveScheduleState(variant);
  }

  // Swatch price is always the delta vs. the currently selected variant —
  // same +/-XXX kr shape as the firmness/top-mattress option cards
  // (_buildOptionCard) — never the fabric's own absolute/member/sale price.
  _priceHtml(variant, delta) {
    if (!variant || !variant.price || delta === null) return '';
    return `<span class="configurator__fabric-price">${this._formatDelta(delta)}</span>`;
  }

  // Returns true if the badge text is a savings claim (e.g. "SPAR 40%", "Restsalg")
  // rather than an informational label (e.g. "NYHED"). Savings claims are suppressed
  // when hideSavings is true or a campaign/member price already shows.
  _isSavingsBadge(badge) {
    if (!badge) return false;
    const lower = badge.toLowerCase();
    return lower.includes('spar') || lower.includes('restsalg') || lower.includes('tilbud');
  }

  // "None" is a selectable value on the badge metaobject field meaning
  // "no badge", not an actual label to render.
  _hasBadge(badge) {
    return !!badge && badge.trim().toLowerCase() !== 'none';
  }

  // One badge per fabric card. A campaign from the feed always wins; the
  // fabric metaobject's own badge ("Bestseller", "Nyhed") is the fallback for
  // fabrics with no campaign data (ClickUp 869f3pqtw).
  _badgesHtml(priceState, info) {
    if (priceState && priceState.showCampaign) {
      const primary = priceState.campaign;
      const style = primary.color ? ` style="--cfg-badge-bg: rgb(${primary.color});"` : '';
      return `<span class="configurator__fabric-badges"><span class="configurator__fabric-badge"${style}>${priceState.campaignText}</span></span>`;
    }
    return this._hasBadge(info.badge) && !(priceState && priceState.memberActive) &&
           !(priceState && priceState.hideSavings && this._isSavingsBadge(info.badge))
      ? `<span class="configurator__fabric-badges"><span class="configurator__fabric-badge">${info.badge}</span></span>`
      : '';
  }

  // Swaps the mobile hero to the tapped fabric's product shot. Falls back to
  // the fabric's environment image; keeps the current hero when neither exists.
  _updateFabricHero(option1) {
    const heroImg = this._modal?.querySelector('[data-fabric-hero]');
    if (!heroImg) return;
    const item = this.fabricData.find((d) => d.option1 === option1);
    if (!item) return;
    const src = item.variantImage || item.richInfo[0]?.secondaryImage;
    if (!src || heroImg.src === src) return;
    heroImg.src = src;
    heroImg.alt = item.richInfo[0]?.name || option1;
  }

  _openFabricDetail(option1) {
    const item = this.fabricData.find((d) => d.option1 === option1);
    if (!item) return;
    const info = item.richInfo[0] || {};
    const name = info.name || option1;
    const material = Array.isArray(info.material) ? info.material.join(', ') : info.material;

    const nameEl = this._modal.querySelector('[data-fabric-detail-name]');
    const imgEl = this._modal.querySelector('[data-fabric-detail-image]');
    const descEl = this._modal.querySelector('[data-fabric-detail-description]');
    const specsEl = this._modal.querySelector('[data-fabric-detail-specs]');
    const secondaryEl = this._modal.querySelector('[data-fabric-detail-secondary]');

    // Figma titles the popup "Name - materiale"
    if (nameEl) nameEl.textContent = material ? `${name} - ${material.toLowerCase()}` : name;
    if (imgEl) {
      imgEl.src = info.image || '';
      imgEl.alt = name;
      imgEl.hidden = !info.image;
    }
    if (descEl) descEl.innerHTML = configuratorRenderRichText(info.description);

    if (specsEl) {
      const specs = [
        info.martindale && { label: 'Martindale – slidstyrke', value: info.martindale },
        info.lightFastness && { label: 'Lysægthed – farvebestandighed', value: info.lightFastness },
        info.pilling && { label: 'Pilling – modstand mod fnugdannelse', value: info.pilling },
      ].filter(Boolean);

      specsEl.innerHTML = specs.map(({ label, value }) =>
        `<div class="configurator__detail-spec"><dt>${label}:</dt><dd>${value}</dd></div>`
      ).join('');
    }

    if (secondaryEl) {
      secondaryEl.src = info.secondaryImage || '';
      secondaryEl.alt = name;
      secondaryEl.hidden = !info.secondaryImage;
    }

    this._showFabricView('detail');
    this._modal.querySelector('[data-fabric-detail-back]')?.focus();
  }

  _applyFabricSelection(option1) {
    this.selectedOptions.option1 = option1;
    this._updateVariantFromOptions('option1');
  }

  // ─── Icon helpers ─────────────────────────────────────────────────────────

  _chevronSvg() {
    // Mirrors snippets/icon-chevron-down.liquid exactly — same viewBox and path,
    // so a JS-rendered sub-row chevron is the same size as a Liquid-rendered one
    // (they sit next to each other on dual-sided firmness rows).
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false" role="presentation" class="icon">
      <path d="M20 8.5 12.5 16 5 8.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    </svg>`;
  }

  _infoIconSvg() {
    // ILVA_Icon_Web_Info — square outline with a serif "i", composed from the
    // brand icon's vectors (Figma node 727:4928-4930) into one 20×20 viewBox.
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false">
      <g fill="currentColor">
        <path transform="translate(3.938 3.938)" d="M0 12.125H12.1208V0H0V12.1208V12.125ZM0.791667 0.791667H11.3292V11.3292H0.791667V0.791667Z"/>
        <path transform="translate(7.496 8.796)" d="M3.11667 0H0.579166V0.791667H2.325V3.90417H0V4.69583H2.325H3.11667H5.275V3.90417H3.11667V0Z"/>
        <path transform="translate(9.316 6.408)" d="M0.7625 1.525C1.18333 1.525 1.525 1.18333 1.525 0.7625C1.525 0.341666 1.18333 0 0.7625 0C0.341667 0 0 0.341666 0 0.7625C0 1.18333 0.341667 1.525 0.7625 1.525Z"/>
      </g>
    </svg>`;
  }
}

customElements.define('product-configurator', ProductConfigurator);
